import { clamp01 } from './geometry'
import './viz.css'

interface ArcGaugeProps {
  value: number | null
  max: number
  tone?: 'brand' | 'gain' | 'warn' | 'loss'
  label: string
  sub?: string
  ariaLabel: string
}

// Semicircle of radius 40 about (50, 50), left end to right end over the top.
const ARC_PATH = 'M 10 50 A 40 40 0 0 1 90 50'

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Half-circle gauge of `value` against `max` (the fill is clamped to 0–100%).
 * A null or non-finite value, or a non-positive or non-finite max, draws the
 * track only. `label` and `sub` are HTML overlaid on the arc's base rather than
 * SVG text, so they stay at their CSS size however far the arc scales.
 *
 * In a module body in the fixed desktop tier the arc scales with the available
 * height (56px at least); elsewhere it is as wide as its parent (see viz.css).
 */
export function ArcGauge({ value, max, tone = 'brand', label, sub, ariaLabel }: ArcGaugeProps) {
  const drawn = value !== null && Number.isFinite(value) && Number.isFinite(max) && max > 0
  const fill = drawn ? round2(clamp01(value / max) * 100) : 0

  return (
    <div className={`viz-arc viz-arc--${tone}`} role="img" aria-label={ariaLabel}>
      <div className="viz-arc-stage">
        <svg
          className="viz-arc-svg"
          viewBox="0 0 100 56"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <path className="viz-arc-track" d={ARC_PATH} pathLength="100" />
          {drawn && (
            <path
              className="viz-arc-fill"
              d={ARC_PATH}
              pathLength="100"
              strokeDasharray={`${fill} 100`}
            />
          )}
        </svg>
        <div className="viz-arc-text">
          <span className="viz-arc-label">{label}</span>
          {sub !== undefined && <span className="viz-arc-sub">{sub}</span>}
        </div>
      </div>
    </div>
  )
}
